import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  defaultParameterSet, type Job, type LayerInventory, type LayerRole, type ParameterSet, type PlanPreview, type Project, type ProjectModel,
} from "@unitly/contracts";
import { api, ApiError, waitForJob } from "./api";
import { ModularMark, Wordmark } from "./brand/Logo";
import { PlanCanvas } from "./canvas/PlanCanvas";
import { DrawingPanel, type DrawingStage, type Settings } from "./steps/DrawingPanel";
import { ParametersPanel } from "./steps/ParametersPanel";
import { formatBytes, uploadDrawing, type UploadProgress } from "./upload/uploader";

function useProjectId(): [string | null, (id: string | null) => void] {
  const read = () => new URLSearchParams(location.search).get("project");
  const [id, setId] = useState<string | null>(read);
  useEffect(() => {
    const onPop = () => setId(read());
    addEventListener("popstate", onPop);
    return () => removeEventListener("popstate", onPop);
  }, []);
  const go = (next: string | null) => {
    const url = new URL(location.href);
    if (next) url.searchParams.set("project", next);
    else url.searchParams.delete("project");
    history.pushState(null, "", url);
    setId(next);
  };
  return [id, go];
}

export function App() {
  const [projectId, go] = useProjectId();
  return projectId ? <Workspace key={projectId} projectId={projectId} onHome={() => go(null)} /> : <Home onOpen={go} />;
}

function TopBar({ children, status, onHome }: { children?: React.ReactNode; status?: React.ReactNode; onHome: () => void }) {
  return (
    <header className="topbar">
      <button onClick={onHome} aria-label="Unitly projects" style={{ border: "none", background: "none", padding: 0, display: "flex" }}>
        <Wordmark size={26} />
      </button>
      <span className="divider" />
      <nav className="crumbs" aria-label="Breadcrumb">{children}</nav>
      {status}
    </header>
  );
}

function Home({ onOpen }: { onOpen: (id: string) => void }) {
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.listProjects().then(setProjects).catch((e) => setError(`Could not reach the Unitly API. ${e.message}`));
  }, []);
  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const p = await api.createProject(name.trim());
      onOpen(p.id);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };
  return (
    <div className="shell" style={{ gridTemplateRows: "56px minmax(0,1fr)" }}>
      <TopBar onHome={() => undefined}><span>Projects</span></TopBar>
      <main className="canvas-area" style={{ overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "center", padding: "56px 20px" }}>
          <div className="projects">
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <ModularMark size={44} />
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <h1 style={{ margin: 0, font: "700 28px var(--font-brand)", letterSpacing: "-0.02em" }}>Projects</h1>
                <span className="muted">Import a floor plan, map its layers and set the brief for the layout solver.</span>
              </div>
            </div>
            <form onSubmit={create} className="card" style={{ padding: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <input className="input" style={{ flex: "1 1 240px" }} placeholder="Project name, for example 15 viviendas Morales" value={name}
                onChange={(e) => setName(e.target.value)} aria-label="Project name" maxLength={120} />
              <button className="btn primary" disabled={busy || !name.trim()}>New project<span className="unit" /></button>
            </form>
            {error && <div className="notice error" role="alert">{error}</div>}
            {projects === null && !error && <span className="faint">Loading projects</span>}
            {projects?.length === 0 && <span className="faint">No projects yet.</span>}
            {projects?.map((p) => (
              <button key={p.id} className="project-row" onClick={() => onOpen(p.id)}>
                <span className="tile" />
                <span style={{ flex: 1, display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
                  <span style={{ font: "600 15px var(--font-brand)" }}>{p.name}</span>
                  <span className="muted" style={{ fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {p.source ? `${p.source.name} · ${formatBytes(p.source.size_bytes)}` : "No drawing yet"} · updated {new Date(p.updated_at).toLocaleDateString()}
                  </span>
                </span>
                <span className={`badge ${p.stage === "modelled" ? "pass" : "warn"}`} style={{ padding: "4px 7px" }}>
                  {{ new: "NEW", uploaded: "UPLOADED", inventoried: "LAYERS", modelled: "MODEL" }[p.stage]}
                </span>
              </button>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

function Workspace({ projectId, onHome }: { projectId: string; onHome: () => void }) {
  const [project, setProject] = useState<Project | null>(null);
  const [inventory, setInventory] = useState<LayerInventory | null>(null);
  const [preview, setPreview] = useState<PlanPreview | null>(null);
  const [model, setModel] = useState<ProjectModel | null>(null);
  const [roles, setRoles] = useState<Record<string, LayerRole>>({});
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [hovered, setHovered] = useState<string | null>(null);
  const [settings, setSettings] = useState<Settings>({ units: null, north_angle_deg: 0, gap_tolerance_m: 0.02 });
  const [stage, setStage] = useState<DrawingStage>({ kind: "empty" });
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const [params, setParams] = useState<ParameterSet>(defaultParameterSet());
  const [paramsSavedAt, setParamsSavedAt] = useState<string | null>(null);
  const [paramsDirty, setParamsDirty] = useState(false);
  const [paramsError, setParamsError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ text: string; error?: boolean } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const uploadAbort = useRef<AbortController | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const flash = (text: string, isError = false) => {
    setToast({ text, error: isError });
    setTimeout(() => setToast(null), 3200);
  };

  const loadInventory = useCallback(async (p: Project) => {
    const [inv, prev] = await Promise.all([api.inventory(projectId), api.preview(projectId)]);
    setInventory(inv);
    setPreview(prev);
    const saved = Object.fromEntries(p.mapping.map((m) => [m.name, m.role]));
    setRoles(Object.fromEntries(inv.layers.map((l) => [l.name, (saved[l.name] ?? l.suggested_role) as LayerRole])));
    setHidden(new Set());
    return inv;
  }, [projectId]);

  const follow = useCallback(async (job: Job) => {
    setStage({ kind: "working", what: job.kind, progress: job.progress ?? null, startedAt: Date.parse(job.created_at) || Date.now() });
    const done = await waitForJob(job, (j) => setStage((s) => (s.kind === "working" ? { ...s, progress: j.progress ?? s.progress } : s)));
    const p = await api.getProject(projectId);
    setProject(p);
    if (done.status === "failed") {
      setError(done.error ?? "The engine could not process the drawing.");
      setStage(p.artifacts.inventory ? { kind: "layers" } : { kind: "empty" });
      if (p.artifacts.inventory && !inventory) await loadInventory(p);
      return;
    }
    if (done.kind === "inventory") {
      await loadInventory(p);
      setModel(null);
      setStage({ kind: "layers" });
    } else {
      setModel(await api.model(projectId));
      setStage({ kind: "model" });
    }
  }, [projectId, loadInventory, inventory]);

  // Initial load, and resuming a job that was running when the page was left.
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const p = await api.getProject(projectId);
        if (!live) return;
        setProject(p);
        setSettings({ units: p.settings.units ?? null, north_angle_deg: p.settings.north_angle_deg, gap_tolerance_m: p.settings.gap_tolerance_m });
        const params = await api.parameters(projectId);
        if (!live) return;
        setParams(params.parameters);
        setParamsSavedAt(params.updated_at);
        if (p.artifacts.inventory) await loadInventory(p);
        if (p.artifacts.model) {
          setModel(await api.model(projectId));
          setStage({ kind: "model" });
        } else if (p.artifacts.inventory) {
          setStage({ kind: "layers" });
        }
        const running = p.jobs.find((j) => j.status === "queued" || j.status === "running");
        if (running) await follow(running);
        else if (p.source && !p.artifacts.inventory) {
          const last = p.jobs.find((j) => j.kind === "inventory");
          if (last?.status === "failed") setError(last.error ?? "Reading the drawing failed.");
          else await follow(await api.startJob(projectId, "inventory"));
        }
      } catch (e) {
        if (live) setError(e instanceof ApiError && e.status === 404 ? "This project does not exist." : `Could not load the project. ${(e as Error).message}`);
      }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const pickFile = async (file: File) => {
    setError(null);
    if (/\.dwg$/i.test(file.name)) return setError("DWG is not supported yet. Save as DXF in AutoCAD.");
    if (!/\.dxf$/i.test(file.name)) return setError("Choose a .dxf file.");
    const ctrl = new AbortController();
    uploadAbort.current = ctrl;
    setStage({ kind: "uploading", file: file.name, progress: null });
    try {
      const p = await uploadDrawing(projectId, file, (progress) => setStage({ kind: "uploading", file: file.name, progress }), ctrl.signal);
      setProject((old) => (old ? { ...old, ...p } : p));
      setInventory(null);
      setPreview(null);
      setModel(null);
      setStep(1);
      await follow(await api.startJob(projectId, "inventory"));
    } catch (e) {
      setStage(inventory ? { kind: model ? "model" : "layers" } : { kind: "empty" });
      if ((e as Error).name !== "AbortError") setError(`Upload failed. ${(e as Error).message}`);
    } finally {
      uploadAbort.current = null;
    }
  };

  const build = async () => {
    setError(null);
    try {
      const layers = Object.entries(roles).map(([name, role]) => ({ name, role }));
      setProject(await api.saveMapping(projectId, { layers, settings }));
      await follow(await api.startJob(projectId, "model"));
    } catch (e) {
      setError((e as Error).message);
      setStage({ kind: "layers" });
    }
  };

  const saveParams = async () => {
    setSaving(true);
    setParamsError(null);
    try {
      const r = await api.saveParameters(projectId, params);
      setParamsSavedAt(r.updated_at);
      setParamsDirty(false);
      flash("Parameters saved");
    } catch (e) {
      const details = e instanceof ApiError && Array.isArray(e.details) ? ` ${(e.details as string[]).slice(0, 3).join("; ")}` : "";
      setParamsError(`${(e as Error).message}${details}`);
    } finally {
      setSaving(false);
    }
  };

  const scaleToM = useMemo(() => {
    const k: Record<string, number> = { mm: 0.001, cm: 0.01, m: 1, in: 0.0254, ft: 0.3048 };
    return settings.units ? k[settings.units] : preview?.scale_to_m ?? 0.001;
  }, [settings.units, preview]);

  const busy = stage.kind === "uploading" || stage.kind === "working";
  const status = (() => {
    if (stage.kind === "uploading") return { color: "var(--unit-blue)", text: `Uploading ${stage.progress ? Math.floor((stage.progress.sent / stage.progress.total) * 100) : 0}%` };
    if (stage.kind === "working") return { color: "#C98A00", text: stage.what === "inventory" ? "Reading drawing" : "Building model" };
    if (step === 2 && paramsDirty) return { color: "#C98A00", text: "Unsaved changes" };
    if (!project?.source) return { color: "var(--n-200)", text: "No drawing" };
    return { color: "var(--success)", text: "Saved" };
  })();

  const steps = [
    { n: 1 as const, title: "Drawing", sub: model ? `${model.openings.windows.length} windows · ${model.boundary.area_m2.toFixed(0)} m²` : inventory ? `${inventory.layers.length} layers` : "Upload a DXF", enabled: true, done: !!model },
    { n: 2 as const, title: "Parameters", sub: paramsSavedAt ? "Saved" : "Preset", enabled: !!model, done: !!paramsSavedAt && !paramsDirty },
  ];

  const empty = stage.kind === "empty" || (stage.kind === "uploading" && !preview);
  const showModelOverlay = !!model && (step === 2 || stage.kind === "model");

  return (
    <div className="shell">
      <TopBar onHome={onHome} status={
        <span className="save-state" aria-live="polite"><span className="sq" style={{ background: status.color }} />{status.text}</span>
      }>
        <button onClick={onHome}>Projects</button><span className="sep">/</span><span>{project?.name ?? ""}</span>
      </TopBar>
      <div className="workspace">
        <nav className="stepnav" aria-label="Workflow">
          <span className="eyebrow">Workflow</span>
          {steps.map((s) => (
            <button key={s.n} className={`step${step === s.n ? " current" : ""}${s.done && step !== s.n ? " done" : ""}`}
              disabled={!s.enabled || busy} onClick={() => setStep(s.n)} aria-current={step === s.n ? "step" : undefined}>
              <span className="num">{s.done && step !== s.n ? "✓" : s.n}</span>
              <span className="txt"><span className="title">{s.title}</span><span className="sub">{s.sub}</span></span>
            </button>
          ))}
          <button className="step" disabled aria-disabled="true">
            <span className="num">3</span>
            <span className="txt"><span className="title">Layout solver</span><span className="sub">Coming next</span></span>
          </button>
          <div className="foot">
            <span className="eyebrow">Floor</span>
            {model ? (
              <>
                <span style={{ color: "var(--ink)" }}>{model.boundary.area_m2.toFixed(1)} m² · {model.boundary.edges.length} edges</span>
                <span>{model.frame.source_units} · north {model.frame.north_angle_deg}°</span>
              </>
            ) : <span>Not built yet</span>}
          </div>
        </nav>

        <main className="canvas-area"
          onDragOver={(e) => { if (!busy) { e.preventDefault(); setDragOver(true); } }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f && !busy) pickFile(f); }}>
          <input ref={fileInput} type="file" accept=".dxf,.dwg" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) pickFile(f); }} />
          {preview && (
            <PlanCanvas preview={preview} roles={roles} hidden={hidden} hovered={hovered}
              model={showModelOverlay ? model : null}
              overlay={{
                viewProtectionM: step === 2 && params.view_protection.enabled ? params.view_protection.depth_m : null,
                wetRadiusM: step === 2 ? params.wet_rooms.max_distance_to_shaft_m : null,
              }}
              scaleToM={scaleToM} northAngle={model?.frame.north_angle_deg ?? settings.north_angle_deg} focusModel={showModelOverlay} />
          )}
          {preview && project?.source && (
            <div className="canvas-chip">
              <span style={{ color: model ? "var(--success)" : "var(--unit-blue)" }}>{model ? "✓" : "■"}</span>
              <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
                {project.source.name} · {formatBytes(project.source.size_bytes)}
              </span>
              {step === 1 && !busy && <button className="btn small" onClick={() => fileInput.current?.click()}>Replace</button>}
            </div>
          )}
          {showModelOverlay && step === 2 && (
            <div className="legend">
              <span><i style={{ background: "repeating-linear-gradient(45deg, #2348FF 0 1px, transparent 1px 5px)", border: "1px dashed #2348FF" }} />View protection {params.view_protection.depth_m.toFixed(1)} m</span>
              <span><i style={{ border: "1px dashed #4F4B43", borderRadius: "50%", width: 12, height: 12 }} />Wet rooms within {params.wet_rooms.max_distance_to_shaft_m.toFixed(1)} m of a shaft</span>
            </div>
          )}
          {showModelOverlay && step === 1 && (
            <div className="legend">
              <span><i style={{ border: "2px solid #2348FF", background: "rgba(35,72,255,.05)" }} />Boundary</span>
              <span><i style={{ background: "#2348FF", height: 4 }} />Window</span>
              <span><i style={{ background: "#E8603C", height: 4 }} />Entrance</span>
              <span><i style={{ background: "#161513" }} />Wall, column</span>
              <span><i style={{ background: "repeating-linear-gradient(45deg, #161513 0 1px, #fff 1px 4px)", border: "1px solid #161513" }} />Shaft</span>
              <span><i style={{ borderTop: "2px dashed #4F4B43", height: 0 }} />Existing partition</span>
            </div>
          )}
          {empty && (
            <div className="center-fill">
              <div className={`dropzone${dragOver ? " over" : ""}`}>
                {stage.kind === "uploading" ? <ModularMark size={56} animate /> : (
                  <div className="types"><span>DXF</span><span className="off" title="Save as DXF in AutoCAD">DWG</span></div>
                )}
                <h2>{stage.kind === "uploading" ? "Uploading the drawing" : "Drop a DXF here"}</h2>
                <span className="muted">Floor plans exported from AutoCAD as DXF · up to 5 GB</span>
                {stage.kind === "empty" && <button className="btn primary" onClick={() => fileInput.current?.click()}>Choose file</button>}
              </div>
            </div>
          )}
          {stage.kind === "working" && !preview && (
            <div className="center-fill"><ModularMark size={64} animate title="Working" /></div>
          )}
        </main>

        {step === 1 ? (
          <DrawingPanel stage={stage} error={error} inventory={inventory} model={model} roles={roles}
            setRole={(name, role) => setRoles((r) => ({ ...r, [name]: role }))}
            hidden={hidden} toggleHidden={(n) => setHidden((h) => { const x = new Set(h); if (x.has(n)) x.delete(n); else x.add(n); return x; })}
            hovered={hovered} setHovered={setHovered} settings={settings} setSettings={setSettings}
            onBuild={build} onEditLayers={() => setStage({ kind: "layers" })} onContinue={() => setStep(2)}
            onCancelUpload={() => uploadAbort.current?.abort()}
            onRetryInventory={async () => { setError(null); try { await follow(await api.startJob(projectId, "inventory")); } catch (e) { setError((e as Error).message); } }} />
        ) : (
          <ParametersPanel params={params} setParams={(p) => { setParams(p); setParamsDirty(true); }} dirty={paramsDirty} saving={saving}
            savedAt={paramsSavedAt} onSave={saveParams} error={paramsError} windows={model?.openings.windows.length ?? 0}
            onReset={() => { setParams(defaultParameterSet()); setParamsDirty(true); }} />
        )}
      </div>
      {toast && <div className={`toast${toast.error ? " error" : ""}`} role="status"><i />{toast.text}</div>}
    </div>
  );
}
